import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { HandHeart, Eye, MapPin, DeviceMobile } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function CampaignTicker() {
  const { pid } = useParams();
  const [sp] = useSearchParams();
  const light = sp.get("theme") === "light";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/campaign-ticker/${pid}`).then((r) => { setData(r.data); setErr(false); }).catch(() => setErr(true));
  }, [pid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const accent = data?.brand === "indiaalumni" ? "#E8873B" : "#2Fb389";
  const cur = data?.brand === "indiaalumni" ? "₹" : "$";
  const bg = light ? "#ffffff" : "#0d1714";
  const fg = light ? "#0d1714" : "#eef4f1";
  const sub = light ? "#5b6b64" : "#93a79d";
  const cardBorder = light ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.08)";

  if (err) return (
    <div style={{ fontFamily: "system-ui, sans-serif", background: bg, color: sub, minHeight: "100vh", display: "grid", placeItems: "center", padding: 20 }} data-testid="ticker-error">
      Campaign not found.
    </div>
  );
  if (!data) return (
    <div style={{ fontFamily: "system-ui, sans-serif", background: bg, color: sub, minHeight: "100vh", display: "grid", placeItems: "center" }} data-testid="ticker-loading">
      Loading live ticker…
    </div>
  );

  const c = data.campaign || {};
  const recent = data.recent || [];
  const topC = data.top_countries || [];
  const maxC = Math.max(1, ...topC.map((x) => x.clicks || 0));

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", background: bg, color: fg, minHeight: "100vh", padding: "22px 20px", boxSizing: "border-box" }} data-testid="campaign-ticker">
      <style>{`@keyframes tkpulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(.8)}}
        @keyframes tkin{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: accent, fontWeight: 700 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent, animation: data.live ? "tkpulse 1.6s ease-in-out infinite" : "none" }} data-testid="ticker-live-dot" />
        {data.live ? "Live" : "Campaign ticker"}
      </div>

      <h1 style={{ margin: "10px 0 2px", fontSize: 20, fontWeight: 650, lineHeight: 1.25 }} data-testid="ticker-title">{c.title}</h1>
      {c.institution && <div style={{ fontSize: 13, color: sub }}>{c.institution}{c.location ? ` · ${c.location}` : ""}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "18px 0" }}>
        <div style={{ border: `1px solid ${cardBorder}`, borderRadius: 14, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: sub }}><Eye size={14} weight="bold" /> Reach</div>
          <div style={{ fontSize: 30, fontWeight: 700, fontVariantNumeric: "tabular-nums" }} data-testid="ticker-reach">{(data.reach || 0).toLocaleString()}</div>
        </div>
        <div style={{ border: `1px solid ${cardBorder}`, borderRadius: 14, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: sub }}><HandHeart size={14} weight="bold" /> Supporters</div>
          <div style={{ fontSize: 30, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: accent }} data-testid="ticker-supporters">{(data.supporters || 0).toLocaleString()}</div>
        </div>
      </div>

      {c.funding_goal > 0 && (
        <div style={{ marginBottom: 18 }} data-testid="ticker-progress">
          <div style={{ height: 8, borderRadius: 99, background: cardBorder, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, c.funded_pct || 0)}%`, background: accent, transition: "width .6s ease" }} />
          </div>
          <div style={{ fontSize: 12, color: sub, marginTop: 6 }}>
            {cur}{Number(c.amount_pledged || 0).toLocaleString()} pledged · {c.funded_pct || 0}% of {cur}{Number(c.funding_goal).toLocaleString()}
          </div>
        </div>
      )}

      {topC.length > 0 && (
        <div style={{ marginBottom: 18 }} data-testid="ticker-countries">
          <div style={{ fontSize: 11, color: sub, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Where interest comes from</div>
          {topC.slice(0, 5).map((x) => (
            <div key={x.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontSize: 13 }}>
              <span style={{ width: 28, color: sub }}>{x.key}</span>
              <div style={{ flex: 1, height: 6, borderRadius: 99, background: cardBorder, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${((x.clicks || 0) / maxC) * 100}%`, background: accent, opacity: 0.85 }} />
              </div>
              <span style={{ color: sub, fontVariantNumeric: "tabular-nums" }}>{x.clicks}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, color: sub, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>Live activity</div>
      {recent.length === 0 ? (
        <div style={{ fontSize: 13, color: sub, border: `1px dashed ${cardBorder}`, borderRadius: 12, padding: "14px 16px" }} data-testid="ticker-empty">
          Waiting for the first visit… shares of this campaign will appear here in real time.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {recent.slice(0, 8).map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "8px 12px", border: `1px solid ${cardBorder}`, borderRadius: 10, animation: "tkin .3s ease" }} data-testid={`ticker-activity-${i}`}>
              <MapPin size={14} style={{ color: accent }} />
              <span>{[s.city, s.country].filter(Boolean).join(", ") || "Someone"}</span>
              {s.device && <span style={{ color: sub, display: "inline-flex", alignItems: "center", gap: 3 }}><DeviceMobile size={12} /> {s.device}</span>}
              <span style={{ marginLeft: "auto", color: sub }}>{timeAgo(s.received_at || s.at)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 18, fontSize: 11, color: sub, textAlign: "center" }}>
        Powered by {data.brand === "indiaalumni" ? "IndiaAlumni" : "AlumniPulse"}
      </div>
    </div>
  );
}
